import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, DollarSign, Sparkles, TrendingUp, RefreshCw, Search, Shield, CreditCard, Wallet, Activity } from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card className="rounded-2xl bg-card border-border/70 p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <div className="h-8 w-8 rounded-xl bg-accent/60 border border-rose-tint flex items-center justify-center">
        <Icon className="h-4 w-4 text-rose-gold" />
      </div>
    </div>
    <p className="mt-3 font-display text-3xl text-ink tabular-nums leading-tight">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </Card>
);

const formatMoney = (amount, currency) => {
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  } catch {
    return `${currency === 'INR' ? '₹' : '$'}${Number(amount || 0).toFixed(2)}`;
  }
};

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsFilter, setPaymentsFilter] = useState('all');

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/stats');
      setStats(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load admin stats');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (q = '') => {
    setUsersLoading(true);
    try {
      const { data } = await api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setUsers(data || []);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadPayments = async (kind = 'all') => {
    setPaymentsLoading(true);
    try {
      const { data } = await api.get(`/admin/payments${kind && kind !== 'all' ? `?kind=${kind}` : ''}`);
      setPayments(data || []);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadStats();
      loadUsers();
      loadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const rev = stats?.revenue || {};
  const revEntries = Object.entries(rev);

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <span className="text-[11px] uppercase tracking-[0.22em] text-rose-gold flex items-center gap-2">
            <Shield className="h-3.5 w-3.5" /> Admin
          </span>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-semibold text-ink leading-[1.05]">
            Operations dashboard.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Real-time snapshot of users, revenue and AI usage.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-xl"
          onClick={() => { loadStats(); loadUsers(userSearch); loadPayments(paymentsFilter); }}
          data-testid="admin-refresh-button"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Top stat cards */}
      {loading ? (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
      ) : stats ? (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total users" value={stats.users.total}
              sub={`+${stats.users.new_7d} in last 7 days`} testId="stat-total-users" />
            <StatCard icon={TrendingUp} label="Paying users" value={stats.users.paying}
              sub={`${stats.users.total > 0 ? ((stats.users.paying / stats.users.total) * 100).toFixed(1) : 0}% conversion`}
              testId="stat-paying-users" />
            <StatCard icon={Wallet} label="Credits outstanding" value={stats.credits.outstanding}
              sub="All users combined" testId="stat-credits-outstanding" />
            <StatCard icon={Activity} label="AI actions" value={
              Object.values(stats.credits.spend_by_feature || {}).reduce((s, v) => s + (v.count || 0), 0)
            } sub="Q&A + Styling + Regimen" testId="stat-ai-actions" />
          </div>

          {/* Revenue cards */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="revenue-section">
            {revEntries.length === 0 ? (
              <Card className="rounded-2xl bg-cream-paper border-border/70 p-5 col-span-full">
                <p className="text-sm text-muted-foreground">No paid orders yet.</p>
              </Card>
            ) : revEntries.map(([cur, r]) => (
              <Card key={cur} className="rounded-2xl bg-card border-border/70 p-5" data-testid={`revenue-${cur}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Revenue ({cur})</p>
                  <div className="h-8 w-8 rounded-xl bg-accent/60 border border-rose-tint flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-rose-gold" />
                  </div>
                </div>
                <p className="mt-3 font-display text-3xl text-ink tabular-nums">{formatMoney(r.amount, cur)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {r.credits_sold} credits sold across {r.orders} order{r.orders === 1 ? '' : 's'}
                </p>
              </Card>
            ))}
          </div>

          {/* AI feature breakdown + content counts */}
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <Card className="rounded-2xl bg-card border-border/70 p-5" data-testid="feature-breakdown">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Credits spent by feature</p>
              <ul className="mt-3 space-y-2">
                {Object.entries(stats.credits.spend_by_feature || {}).map(([feat, v]) => (
                  <li key={feat} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{feat.replace(/_/g, ' ')}</span>
                    <span className="tabular-nums text-muted-foreground">{v.credits} cr · {v.count} uses</span>
                  </li>
                ))}
                {Object.keys(stats.credits.spend_by_feature || {}).length === 0 && (
                  <li className="text-sm text-muted-foreground">No AI usage yet.</li>
                )}
              </ul>
            </Card>
            <Card className="rounded-2xl bg-card border-border/70 p-5" data-testid="content-counts">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Catalog</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>Products: <span className="font-semibold">{stats.content.products}</span></div>
                <div>DIY hacks: <span className="font-semibold">{stats.content.hacks}</span></div>
                <div>Reviews: <span className="font-semibold">{stats.content.reviews}</span></div>
                <div>Favorites: <span className="font-semibold">{stats.content.favorites}</span></div>
              </div>
            </Card>
          </div>

          {/* Tabs: Recent purchases / Users / Ledger */}
          <Tabs defaultValue="purchases" className="mt-10">
            <TabsList className="bg-secondary rounded-xl p-1 border border-border/70 grid grid-cols-3 max-w-lg">
              <TabsTrigger value="purchases" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="admin-tab-purchases">
                <CreditCard className="h-3.5 w-3.5 mr-1.5" /> Purchases
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="admin-tab-users">
                <Users className="h-3.5 w-3.5 mr-1.5" /> Users
              </TabsTrigger>
              <TabsTrigger value="ledger" className="rounded-lg data-[state=active]:bg-card text-xs" data-testid="admin-tab-ledger">
                <Activity className="h-3.5 w-3.5 mr-1.5" /> Ledger
              </TabsTrigger>
            </TabsList>

            <TabsContent value="purchases" className="mt-6">
              <Card className="rounded-2xl bg-card border-border/70 p-4 sm:p-6">
                <ScrollArea className="max-h-[480px]">
                  {(stats.recent_purchases || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">No purchases yet.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        <tr className="border-b border-border/60">
                          <th className="text-left py-2 pr-3">User</th>
                          <th className="text-left py-2 px-3">Package</th>
                          <th className="text-right py-2 px-3">Amount</th>
                          <th className="text-right py-2 px-3">Credits</th>
                          <th className="text-right py-2 pl-3">When</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {stats.recent_purchases.map((p) => (
                          <tr key={p.id} data-testid="purchase-row">
                            <td className="py-2 pr-3">
                              <div className="font-medium">{p.user_name || p.user_email || 'Unknown'}</div>
                              <div className="text-xs text-muted-foreground">{p.user_email}</div>
                            </td>
                            <td className="py-2 px-3 capitalize">{p.package_id}</td>
                            <td className="py-2 px-3 text-right tabular-nums">
                              {formatMoney((p.amount_minor || 0) / 100, p.currency)}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums">+{p.credits}</td>
                            <td className="py-2 pl-3 text-right text-xs text-muted-foreground">
                              {new Date(p.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            <TabsContent value="users" className="mt-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && loadUsers(userSearch)}
                    placeholder="Search by email or name"
                    className="pl-9 h-10 rounded-xl bg-card"
                    data-testid="admin-user-search"
                  />
                </div>
                <Button variant="outline" className="rounded-xl" onClick={() => loadUsers(userSearch)} data-testid="admin-user-search-button">
                  Search
                </Button>
              </div>
              <Card className="rounded-2xl bg-card border-border/70 p-4 sm:p-6">
                <ScrollArea className="max-h-[520px]">
                  {usersLoading ? (
                    <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
                  ) : users.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">No users found.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        <tr className="border-b border-border/60">
                          <th className="text-left py-2 pr-3">User</th>
                          <th className="text-left py-2 px-3">Profile</th>
                          <th className="text-right py-2 px-3">Credits</th>
                          <th className="text-right py-2 pl-3">Joined</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {users.map((u) => (
                          <tr key={u.id} data-testid="user-row">
                            <td className="py-2 pr-3">
                              <div className="font-medium flex items-center gap-1.5">
                                {u.name || '—'}
                                {u.is_admin && <Badge className="bg-rose-tint/60 text-ink border border-rose-tint text-[9px] uppercase">Admin</Badge>}
                              </div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">
                              {[u.skin_type, u.region, u.lifestyle].filter(Boolean).join(' · ') || '—'}
                            </td>
                            <td className="py-2 px-3 text-right tabular-nums font-semibold">{u.credits ?? 0}</td>
                            <td className="py-2 pl-3 text-right text-xs text-muted-foreground">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>

            <TabsContent value="ledger" className="mt-6">
              <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'purchase', 'spend', 'refund', 'welcome'].map((k) => (
                  <Button
                    key={k}
                    variant={paymentsFilter === k ? 'default' : 'outline'}
                    size="sm"
                    className="rounded-full capitalize"
                    onClick={() => { setPaymentsFilter(k); loadPayments(k); }}
                    data-testid={`ledger-filter-${k}`}
                  >
                    {k}
                  </Button>
                ))}
              </div>
              <Card className="rounded-2xl bg-card border-border/70 p-4 sm:p-6">
                <ScrollArea className="max-h-[520px]">
                  {paymentsLoading ? (
                    <div className="space-y-2">{[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
                  ) : payments.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">No ledger entries.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        <tr className="border-b border-border/60">
                          <th className="text-left py-2 pr-3">User</th>
                          <th className="text-left py-2 px-3">Kind</th>
                          <th className="text-left py-2 px-3">Detail</th>
                          <th className="text-right py-2 px-3">Credits</th>
                          <th className="text-right py-2 pl-3">When</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {payments.map((p) => (
                          <tr key={p.id || `${p.created_at}-${p.kind}`} data-testid="ledger-row">
                            <td className="py-2 pr-3 text-xs text-muted-foreground">
                              {p.user_email || p.user_id || '—'}
                            </td>
                            <td className="py-2 px-3 capitalize">{p.kind}</td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">
                              {p.feature || p.package_id || p.note || p.reason || (p.order_id ? p.order_id.slice(0, 18) + '…' : '')}
                              {p.amount_minor != null && p.currency && ` · ${formatMoney(p.amount_minor / 100, p.currency)}`}
                            </td>
                            <td className={`py-2 px-3 text-right tabular-nums font-semibold ${
                              (p.credits || 0) > 0 ? 'text-emerald-700' : (p.credits || 0) < 0 ? 'text-foreground/80' : 'text-muted-foreground'
                            }`}>
                              {(p.credits || 0) > 0 ? '+' : ''}{p.credits ?? 0}
                            </td>
                            <td className="py-2 pl-3 text-right text-xs text-muted-foreground">
                              {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </ScrollArea>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <Card className="mt-8 rounded-2xl bg-cream-paper border-border/70 p-10 text-center">
          <p className="text-sm text-muted-foreground">Could not load dashboard. Refresh to try again.</p>
        </Card>
      )}
    </div>
  );
}
